import { keyframes } from '@mui/material'

/**
 * Motion トークン
 *
 * 一次情報: hub/design-book/01-foundations/motion.md
 *           (mirror: docs/design-system/03_ui_implementation_spec.md §1.5)
 *
 * これまで duration / easing は各 component にベタ書きされていた (PulsingDot・
 * SoapUpdateChat・ContextPane のリロード回転)。 新規のアニメーションはここを参照する
 * (直書き禁止 = token-rules)。 既存のベタ書きは次に触る機会に寄せる。
 */
export const DURATION = {
  instant: 0,
  fast: 120, // ホバー
  base: 180, // モーダル・トースト出現
  slow: 250, // サイドバー開閉
} as const

export const EASING = {
  default: 'cubic-bezier(0.4, 0, 0.2, 1)', // 標準
  out: 'cubic-bezier(0.0, 0, 0.2, 1)', // 出現
  in: 'cubic-bezier(0.4, 0, 1, 1)', // 退出
} as const

// to は書かない。 終端は要素自身の静止値に落ちる (from のみの keyframes は
// 欠けた側が computed style で補われる)。 `to { opacity: 1 }` と固定すると、
// fill-mode: both が再生後もそれを保持し続け、 静止値が 1 未満の要素
// (面取りマークの左面 = 0.45) を上書きして面取りが消える。
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
`

/**
 * 起動モーション (ADR-0026) の一拍分の sx を返す。 opacity 0 → 1 のみで transform は使わない。
 *
 * - duration / delay は **DURATION の語彙から引く** (語彙外の中間値を作らない)。
 * - fill-mode は `both` 固定。 delay 中に一瞬見えてから消える chattering を防ぐ。
 * - opacity のみだが**出現の演出なので reduced-motion では止める** (hover 等の状態遷移とは
 *   区別する。 弱めた動きは前庭障害・片頭痛への対策にならない)。
 * - 再生を起動時だけに限る判定は `useBootReveal()` (`src/shared/hooks/`) 側で行う。
 *   素で使うとマウントのたびに再生する。
 * - 常時走るアニメーションには使わない — 録音中の PulsingDot が「いま動いている = 状態がある」
 *   唯一のサインなので、 chrome が動き続けるとその意味が薄まる。
 */
export const fadeInOnce = (durationMs: number, delayMs = 0) => ({
  animation: `${fadeIn} ${durationMs}ms ${EASING.out} ${delayMs}ms both`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
})
